import type { TFunction } from 'i18next'
import { z } from 'zod'

import {
  EMPLOYMENT_STATUS,
  OCCUPATION,
  type EmploymentStatus,
} from '@/constants/user-profile'
import occupationListEN from '@/utils/locales/en/occupations.json'
import occupationListFR from '@/utils/locales/fr/occupations_fr.json'

const OTHER_PREFIX = 'other:'
const SELF_EMPLOYED_PREFIX = 'self employed:'

const normalizeStatus = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

const STATUS_ALIASES: Record<string, EmploymentStatus> = {
  employed: EMPLOYMENT_STATUS.EMPLOYED,
  salarie: EMPLOYMENT_STATUS.EMPLOYED,
  salariee: EMPLOYMENT_STATUS.EMPLOYED,
  'self-employed': EMPLOYMENT_STATUS.SELF_EMPLOYED,
  'travailleur autonome': EMPLOYMENT_STATUS.SELF_EMPLOYED,
  retired: EMPLOYMENT_STATUS.RETIRED,
  retraite: EMPLOYMENT_STATUS.RETIRED,
  retraitee: EMPLOYMENT_STATUS.RETIRED,
  unemployed: EMPLOYMENT_STATUS.UNEMPLOYED,
  'sans emploi': EMPLOYMENT_STATUS.UNEMPLOYED,
  student: EMPLOYMENT_STATUS.STUDENT,
  etudiant: EMPLOYMENT_STATUS.STUDENT,
  etudiante: EMPLOYMENT_STATUS.STUDENT,
}

export const CAPTURE_DOB_ROUTE = '/SignupUserInfo'

const trimmed = z.string().trim()

export const occupationSchema = z.object({
  employmentStatus: trimmed,
  occupation: trimmed,
  typedOccupation: trimmed,
  occupationDetails: trimmed,
})

export type OccupationFormValues = z.infer<typeof occupationSchema>

export const toCanonicalOccupation = (displayValue: string): string => {
  const trimmed = displayValue.trim()
  if (!trimmed || localStorage.getItem('lang') !== 'fr') return trimmed
  const index = occupationListFR.OCCUPATIONS.indexOf(trimmed)
  return index >= 0 ? occupationListEN.OCCUPATIONS[index] : ''
}

export const OCCUPATION_DEFAULTS: OccupationFormValues = {
  employmentStatus: '',
  occupation: '',
  typedOccupation: '',
  occupationDetails: '',
}

const isEmployedLike = (employmentStatus: string): boolean =>
  employmentStatus === EMPLOYMENT_STATUS.EMPLOYED ||
  employmentStatus === EMPLOYMENT_STATUS.SELF_EMPLOYED

export const refineOccupation = (
  values: OccupationFormValues,
  ctx: z.RefinementCtx,
  t: TFunction
): void => {
  if (isEmployedLike(values.employmentStatus)) {
    if (!values.occupation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['occupation'],
        message: t('occupation.error.occupationRequired'),
      })
    } else if (
      values.occupation === OCCUPATION.OTHER &&
      values.occupationDetails.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['occupationDetails'],
        message: t('occupation.error.occupationDetailsRequired'),
      })
    }
  } else if (values.employmentStatus.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['employmentStatus'],
      message: t('occupation.error.employmentStatusRequired'),
    })
  }
}

export const buildOccupationSchema = (t: TFunction) =>
  occupationSchema.superRefine((values, ctx) =>
    refineOccupation(values, ctx, t)
  )

export const packOccupation = ({
  employmentStatus,
  occupation,
  occupationDetails,
}: Pick<
  OccupationFormValues,
  'employmentStatus' | 'occupation' | 'occupationDetails'
>): string => {
  if (!isEmployedLike(employmentStatus)) {
    return employmentStatus.trim().toLowerCase()
  }

  const base =
    occupation === OCCUPATION.OTHER
      ? `${OTHER_PREFIX}${occupationDetails.trim()}`
      : occupation.trim()
  const withPrefix =
    employmentStatus === EMPLOYMENT_STATUS.SELF_EMPLOYED
      ? `${SELF_EMPLOYED_PREFIX}${base}`
      : base
  return withPrefix.trim().toLowerCase()
}

const unpackInner = (
  value: string
): Pick<
  OccupationFormValues,
  'occupation' | 'typedOccupation' | 'occupationDetails'
> =>
  value.toLowerCase().startsWith(OTHER_PREFIX)
    ? {
        occupation: OCCUPATION.OTHER,
        typedOccupation: OCCUPATION.OTHER,
        occupationDetails: value.slice(OTHER_PREFIX.length).trim(),
      }
    : { occupation: value, typedOccupation: value, occupationDetails: '' }

export const unpackOccupation = (stored: string): OccupationFormValues => {
  const value = stored.trim()
  if (value === '') return { ...OCCUPATION_DEFAULTS }

  if (value.toLowerCase().startsWith(SELF_EMPLOYED_PREFIX)) {
    return {
      employmentStatus: EMPLOYMENT_STATUS.SELF_EMPLOYED,
      ...unpackInner(value.slice(SELF_EMPLOYED_PREFIX.length).trim()),
    }
  }

  const matchedStatus = STATUS_ALIASES[normalizeStatus(value)]
  if (matchedStatus) {
    return {
      employmentStatus: matchedStatus,
      occupation: '',
      typedOccupation: '',
      occupationDetails: '',
    }
  }

  return { employmentStatus: EMPLOYMENT_STATUS.EMPLOYED, ...unpackInner(value) }
}
